import { Mesh, MeshStandardMaterial } from "three";

import type { ResourceId, Square } from "src/api";

import { instantiateModel } from "../../../../render/models/instantiateModel";
import { WorldObject } from "../../../../render/world/WorldObject";
import { kCenteralLoader } from "../../../../singletons/CentralLoader";
import { squareToWorld } from "../../utilities/boardCoordinates";
import {
  DEPOSIT_BASE_DEFINITIONS,
  type DepositModelDefinition,
  type DepositSize,
  RESOURCE_DEPOSIT_DEFINITIONS
} from "./resourceDepositDefinitions";

export class ResourceDeposit extends WorldObject {
  readonly resource: ResourceId;
  private size: DepositSize | null = null;

  // TODO: temp this is so we can recolor the diamond ore model we currenlty have for other materials
  private temporaryOreMaterial: MeshStandardMaterial | null = null;

  constructor(resource: ResourceId, square: Square, fraction: number) {
    super();

    this.resource = resource;

    this.root.position.copy(squareToWorld(square));
    this.setFraction(fraction);
  }

  setFraction(fraction: number) {
    const size = this.sizeForFraction(fraction);
    if (size === this.size) return;

    this.size = size;
    this.rebuildModel(size);
  }

  dispose() {
    this.clearModel();
  }

  private rebuildModel(size: DepositSize) {
    this.clearModel();

    const definition = RESOURCE_DEPOSIT_DEFINITIONS[this.resource];
    const base = this.instantiateDepositModel(DEPOSIT_BASE_DEFINITIONS[size]);
    const ore = this.instantiateDepositModel(definition.oreModels[size]);

    // TODO:
    if (definition.temporaryOreColor) {
      const temporaryOreMaterial = new MeshStandardMaterial({
        color: definition.temporaryOreColor
      });

      ore.traverse((object) => {
        if (object instanceof Mesh) object.material = temporaryOreMaterial;
      });

      this.temporaryOreMaterial = temporaryOreMaterial;
    }

    this.root.add(base, ore);
  }

  private instantiateDepositModel(modelDefinition: DepositModelDefinition) {
    const instance = instantiateModel(
      kCenteralLoader.getLoadedModel(modelDefinition.modelUrl)
    );

    if (modelDefinition.scale !== undefined)
      instance.scale.setScalar(modelDefinition.scale);

    return instance;
  }

  private sizeForFraction(fraction: number): DepositSize {
    if (fraction < 1 / 3) return "small";
    if (fraction < 2 / 3) return "medium";
    return "big";
  }

  private clearModel() {
    this.root.clear();
    this.temporaryOreMaterial?.dispose();
    this.temporaryOreMaterial = null;
  }
}
